/**
 * List-screen kit (DESIGN_SYSTEM.md §6–7): the small pieces every list/table
 * page shares so they read as one calm pattern —
 *   PageHeader → FilterBar (search · compact filters · count/view) → table → pagination.
 *
 * Presentation only: nothing here fetches, sorts or filters data.
 */
import type { ReactNode, SelectHTMLAttributes, MouseEvent } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Filter controls                                                     */
/* ------------------------------------------------------------------ */

interface ListsSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  /** Accessible name. The first option carries the visible meaning ("All statuses"). */
  label: string;
  className?: string;
}

/** Compact filter select for a FilterBar: no stacked label, accessible name via aria-label. */
export function ListsSelect({ label, className = '', children, ...rest }: ListsSelectProps) {
  return (
    <select
      aria-label={label}
      title={label}
      className={`input w-auto min-w-32 flex-1 sm:flex-none sm:min-w-36 sm:max-w-56 ${className}`}
      {...rest}
    >
      {children}
    </select>
  );
}

/** Result count shown at the end of the FilterBar. */
export function ListsCount({ children }: { children: ReactNode }) {
  return (
    <span className="text-sm text-surface-600 tabular-nums whitespace-nowrap" aria-live="polite">
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Table pieces                                                        */
/* ------------------------------------------------------------------ */

export type SortDir = 'asc' | 'desc';

/** aria-sort value for a sortable <th>. */
export function ariaSort(active: boolean, dir: SortDir): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

interface ListsSortButtonProps {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  /** Accessible hint, e.g. "Sort by". */
  sortHint?: string;
}

/** Header button for a sortable column. Put it inside a <th aria-sort={ariaSort(...)}>. */
export function ListsSortButton({ label, active, dir, onClick, sortHint }: ListsSortButtonProps) {
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={onClick}
      title={sortHint ? `${sortHint}: ${label}` : undefined}
      className={`group inline-flex items-center gap-1 rounded-md -mx-1 px-1 transition-colors duration-150 hover:text-surface-900 ${active ? 'text-surface-900' : ''}`}
    >
      {label}
      <Icon
        size={12}
        aria-hidden="true"
        className={active ? 'text-surface-700' : 'text-surface-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-150'}
      />
    </button>
  );
}

interface ListsIconButtonProps {
  label: string;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  /** `danger` stays neutral at rest and only turns red on hover/focus. */
  tone?: 'neutral' | 'danger';
  disabled?: boolean;
  className?: string;
}

/**
 * Quiet row action. Stops row-click propagation so it never opens the row.
 * Destructive actions use tone="danger": neutral at rest, red only on intent.
 */
export function ListsIconButton({ label, onClick, children, tone = 'neutral', disabled, className = '' }: ListsIconButtonProps) {
  const toneCls = tone === 'danger'
    ? 'hover:bg-danger-50 hover:text-danger-700 focus-visible:text-danger-700'
    : 'hover:bg-surface-100 hover:text-surface-900';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      onKeyDown={(e) => e.stopPropagation()}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-surface-500 transition-colors duration-150 disabled:opacity-40 disabled:pointer-events-none ${toneCls} ${className}`}
    >
      {children}
    </button>
  );
}

/** Row actions cell content: right after the data, quiet, end-aligned; above a stretched row link (`dt-above`). */
export function ListsRowActions({ children }: { children: ReactNode }) {
  return <div className="dt-above flex items-center justify-end gap-0.5">{children}</div>;
}

/** Skeleton rows inside the standard table surface (loading state). */
export function ListsTableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="table-container" aria-busy="true">
      <div className="h-10 bg-surface-50 border-b border-surface-200" />
      <div className="divide-y divide-surface-100">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-6 px-4 py-4 animate-pulse">
            {Array.from({ length: cols }).map((__, c) => (
              <div
                key={c}
                className={`h-3 rounded-xs bg-surface-100 ${c === 0 ? 'w-40' : 'flex-1'} ${c > 1 ? 'hidden md:block' : ''}`}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Surface that hosts empty / error states, visually identical to the table surface. */
export function ListsPanel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`bg-surface-0 border border-surface-200 rounded-xl ${className}`}>{children}</div>;
}
